import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  FaPhone,
  FaEnvelope,
  FaWhatsapp,
  FaMapMarkerAlt,
  FaClock,
  FaUser,
  FaPaperPlane,
  FaCheckCircle,
  FaTimes,
  FaIndustry,
  FaPaintBrush,
  FaCog,
  FaBus,
  FaArrowRight
} from 'react-icons/fa';
import './Contact.css';
import { Helmet } from 'react-helmet';
import emailjs from 'emailjs-com';

import { useLocation } from 'react-router-dom';

const Contact = () => {
  const location = useLocation();
  const form = React.useRef();
  const [activeTab, setActiveTab] = useState('general'); // 'general' or 'career'

  // Set active tab based on navigation state
  React.useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);

  const [formData, setFormData] = useState({
    from_name: '',
    from_email: '',
    mobile_no: '',
    message: '',
    position: '' // Only for career
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Enhanced State
  const [errorMessage, setErrorMessage] = useState('');
  const [loadingText, setLoadingText] = useState('Sending...');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    // Validate file if career tab
    if (activeTab === 'career') {
      const fileInput = form.current.querySelector('input[name="resume"]');
      if (fileInput && fileInput.files[0]) {
        if (fileInput.files[0].size > 5 * 1024 * 1024) { // 5MB limit
          setIsSubmitting(false);
          setSubmitStatus('error');
          setErrorMessage('File is too large. Max size is 5MB.');
          return;
        }
      }
    }

    const serviceID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
    const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
    const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

    let resumeUrl = '';

    try {
      // 1. Upload Resume
      if (activeTab === 'career') {
        const fileInput = form.current.querySelector('input[name="resume"]');
        if (fileInput && fileInput.files[0]) {
          setLoadingText('Uploading Resume...'); // Feedback
          const file = fileInput.files[0];
          const formData = new FormData();
          formData.append('file', file);
          formData.append('upload_preset', uploadPreset);

          const res = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/upload`, {
            method: 'POST',
            body: formData
          });

          if (!res.ok) {
            const errorData = await res.json();
            console.error("Cloudinary Error Details:", errorData);
            throw new Error(`Upload failed: ${errorData.error?.message || res.statusText}`);
          }

          const data = await res.json();
          resumeUrl = data.secure_url;
        }
      }

      // 2. Send Email
      setLoadingText('Sending Application...'); // Feedback

      // Construct the unified message for the template
      const baseMessage = `Name: ${formData.from_name}\nPhone: ${formData.mobile_no}\nEmail: ${formData.from_email}\nMessage: ${formData.message}`;

      const finalMessage = activeTab === 'career'
        ? `[JOB APPLICATION]\nPosition: ${formData.position}\n${baseMessage}\nResume Link: ${resumeUrl}`
        : `[GENERAL INQUIRY]\n${baseMessage}`;

      const templateParams = {
        from_name: formData.from_name,
        from_email: formData.from_email,
        mobile_no: formData.mobile_no,
        message: finalMessage,
        position: formData.position
      };

      await emailjs.send(serviceID, templateID, templateParams, publicKey);

      setSubmitStatus('success');
      setFormData({ from_name: '', from_email: '', mobile_no: '', message: '', position: '' });
      if (form.current) form.current.reset();

    } catch (error) {
      console.error("Submission Error:", error);
      setSubmitStatus('error');
      setErrorMessage(error.message || 'Something went wrong. Please check your internet connection or try again later.');
    } finally {
      setIsSubmitting(false);
      // Clear success/error message after 8 seconds
      setTimeout(() => {
        setSubmitStatus(null);
        setErrorMessage('');
      }, 8000);
    }
  };

  const handleContactClick = (type) => {
    switch (type) {
      case 'phone':
        window.open('tel:+97444170859', '_self');
        break;
      case 'email':
        window.open('mailto:abrajaldhafna2014@gmail.com', '_self');
        break;
      case 'whatsapp':
        window.open('https://wa.me/97455994434', '_blank');
        break;
      default:
        break;
    }
  };

  const contactInfo = [
    {
      icon: <FaPhone />,
      title: 'Phone',
      value: '+947 44170859',
      action: () => handleContactClick('phone'),
      color: '#2563eb'
    },
    {
      icon: <FaEnvelope />,
      title: 'Email',
      value: 'abrajaldhafna2014@gmail.com',
      action: () => handleContactClick('email'),
      color: '#2563eb'
    },
    {
      icon: <FaWhatsapp />,
      title: 'WhatsApp',
      value: '+94755994434',
      action: () => handleContactClick('whatsapp'),
      color: '#2563eb'
    },
    {
      icon: <FaMapMarkerAlt />,
      title: 'Location',
      value: 'Qatar',
      action: null,
      color: '#2563eb'
    },
    {
      icon: <FaClock />,
      title: 'Working Hours',
      value: '24/7 Available',
      action: null,
      color: '#2563eb'
    }
  ];

  const services = [
    {
      icon: <FaIndustry />,
      title: 'Construction Manpower Supply Qatar',
      description: 'Skilled labor for construction Doha, certified tradesmen Qatar, and reliable manpower Qatar'
    },
    {
      icon: <FaPaintBrush />,
      title: 'Painting Contractors Qatar',
      description: 'Residential painting services Doha, commercial painting services Qatar, and industrial painting Doha'
    },
    {
      icon: <FaCog />,
      title: 'MEP Specialists Qatar',
      description: 'MEP maintenance Qatar and MEP specialists for complex mechanical, electrical, and plumbing systems'
    },
    {
      icon: <FaBus />,
      title: 'Staff Transportation Services Qatar',
      description: '15-seater worker bus and reliable transportation for construction workers across all Qatar locations'
    }
  ];

  return (
    <div className="contact-page">
      <Helmet>
        <title>Contact Construction Manpower Supply Qatar | Abraj Al Dhafna</title>

        <meta
          name="description"
          content="Contact Abraj Al Dhafna for immediate construction manpower supply in Qatar. 24/7 support for civil, MEP, & industrial labor. Call +974 4417 0859 or visit our Doha office."
        />

        <meta name="keywords" content="contact manpower supply Qatar, construction agency phone number Doha, hire construction workers Qatar, Abraj Al Dhafna contact, office location Doha, manpower supply WhatsApp Qatar" />

        <link rel="canonical" href="https://abrajaldhafna.com/contact" />

        {/* Local Business & ContactPage Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "ContactPage",
                "@id": "https://abrajaldhafna.com/contact#webpage",
                "url": "https://abrajaldhafna.com/contact",
                "name": "Contact Abraj Al Dhafna",
                "isPartOf": { "@id": "https://abrajaldhafna.com/#website" }
              },
              {
                "@type": "LocalBusiness",
                "@id": "https://abrajaldhafna.com/#localbusiness",
                "name": "Abraj Al Dhafna Trading & Contracting WLL",
                "url": "https://abrajaldhafna.com",
                "logo": "https://abrajaldhafna.com/logo.jpeg",
                "telephone": "+97444170859",
                "email": "abrajaldhafna2014@gmail.com",
                "address": {
                  "@type": "PostalAddress",
                  "addressLocality": "Doha",
                  "addressCountry": "QA"
                },
                "contactPoint": {
                  "@type": "ContactPoint",
                  "telephone": "+97444170859",
                  "contactType": "customer service",
                  "availableLanguage": ["en", "ar"]
                },
                "areaServed": "Qatar",
                "sameAs": [
                  "https://abrajaldhafna.com"
                ]
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="contact-hero py-20 bg-primary-blue">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="contact-hero-title text-white mb-6">
              Contact <span className="text-secondary-gold">Us</span>
            </h1>
            <p className="contact-hero-subtitle text-white">
              Get in touch with us for construction manpower supply Qatar, skilled labor for construction Doha, and 24/7 manpower supply Qatar
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Overview */}
      {/* <section className="services-overview py-16 bg-light-gray">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="section-title">
              Our <span className="text-primary-blue">Services</span>
            </h2>
            <p className="section-subtitle">
              From construction manpower supply Qatar to specialized industrial services
            </p>
          </motion.div>

          <div className="services-grid">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="service-card"
              >
                <div className="service-icon">
                  {service.icon}
                </div>
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Blog CTA Section */}
      <section className="blog-cta-section py-16 bg-light-gray">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="section-title mb-6">
              Learn More About Our <span className="text-primary-blue">Services</span>
            </h2>
            <p className="section-subtitle mb-8">
              Check out our latest blog posts and articles about construction manpower, skilled labor, industry trends, and best practices.
            </p>
            <motion.a
              href="/blog"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn btn-primary"
            >
              Read Our Blog <FaArrowRight style={{ marginLeft: '8px' }} />
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="contact-content py-20">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="contact-form-section"
            >
              {/* TABS */}
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', borderBottom: '2px solid #e2e8f0' }}>
                <button
                  onClick={() => setActiveTab('general')}
                  style={{
                    padding: '10px 20px',
                    background: 'none',
                    border: 'none',
                    borderBottom: activeTab === 'general' ? '3px solid #2563eb' : '3px solid transparent',
                    color: activeTab === 'general' ? '#2563eb' : '#64748b',
                    fontWeight: '700',
                    cursor: 'pointer',
                    fontSize: '1rem'
                  }}
                >
                  General Inquiry
                </button>
                <button
                  onClick={() => setActiveTab('career')}
                  style={{
                    padding: '10px 20px',
                    background: 'none',
                    border: 'none',
                    borderBottom: activeTab === 'career' ? '3px solid #2563eb' : '3px solid transparent',
                    color: activeTab === 'career' ? '#2563eb' : '#64748b',
                    fontWeight: '700',
                    cursor: 'pointer',
                    fontSize: '1rem'
                  }}
                >
                  Join Our Team
                </button>
              </div>

              <div className="contact-badge contact-badge-blue">
                {activeTab === 'general' ? 'We respond within 1 hour' : 'We are hiring!'}
              </div>
              <h2 className="section-title mb-8">
                {activeTab === 'general' ? (
                  <>Send Us a <span className="text-primary-blue">Message</span></>
                ) : (
                  <>Apply for a <span className="text-primary-blue">Job</span></>
                )}
              </h2>

              <form ref={form} onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="from_name" className="form-label">
                    <FaUser className="form-icon" />
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="from_name"
                    name="from_name"
                    value={formData.from_name}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                    placeholder="Enter your full name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="from_email" className="form-label">
                    <FaEnvelope className="form-icon" />
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="from_email"
                    name="from_email"
                    value={formData.from_email}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                    placeholder="Enter your email address"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="mobile_no" className="form-label">
                    <FaPhone className="form-icon" />
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="mobile_no"
                    name="mobile_no"
                    value={formData.mobile_no}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                    placeholder="Enter your phone number"
                  />
                </div>

                {/* FIELDS SPECIFIC TO CAREER TAB */}
                {activeTab === 'career' && (
                  <>
                    <div className="form-group">
                      <label htmlFor="position" className="form-label">
                        <FaIndustry className="form-icon" />
                        Position Applied For *
                      </label>
                      <input
                        type="text"
                        id="position"
                        name="position"
                        value={formData.position}
                        onChange={handleInputChange}
                        className="form-input"
                        required
                        placeholder="e.g. Electrician, Civil Engineer, Helper"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="resume" className="form-label">
                        <FaPaperPlane className="form-icon" />
                        Resume (PDF/Doc) *
                      </label>
                      <input
                        type="file"
                        id="resume"
                        name="resume"
                        accept=".pdf,.doc,.docx"
                        className="form-input"
                        required
                      />
                      <small style={{ color: '#666', fontSize: '0.8rem' }}>Max size 2MB.</small>
                    </div>
                  </>
                )}

                <div className="form-group">
                  <label htmlFor="message" className="form-label">
                    <FaPaperPlane className="form-icon" />
                    {activeTab === 'general' ? 'Message *' : 'Cover Letter / Message'}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    className="form-textarea"
                    required={activeTab === 'general'}
                    rows="5"
                    placeholder={activeTab === 'general' ? "Tell us about your manpower requirements..." : "Briefly describe your experience..."}
                  ></textarea>
                </div>

                {/* Progress Bar */}
                {isSubmitting && (
                  <div className="progress-bar" style={{ width: '100%', height: '4px', background: '#2563eb', marginBottom: '1rem', animation: 'progressBarAnim 2s linear infinite' }}></div>
                )}

                <motion.button
                  type="submit"
                  className="btn btn-primary btn-lg submit-btn"
                  disabled={isSubmitting}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {isSubmitting ? (
                    <>
                      <div className="loading-spinner"></div>
                      {loadingText}
                    </>
                  ) : (
                    <>
                      <FaPaperPlane />
                      {activeTab === 'general' ? 'Send Message' : 'Submit Application'}
                    </>
                  )}
                </motion.button>

                {submitStatus === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="success-message"
                  >
                    <FaCheckCircle />
                    <span>
                      {activeTab === 'general'
                        ? "Message sent successfully! We'll get back to you soon."
                        : "Application submitted successfully! Good luck."}
                    </span>
                  </motion.div>
                )}

                {submitStatus === 'error' && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="error-message"
                  >
                    <FaTimes />
                    <span>{errorMessage || "Submission failed. Please try again."}</span>
                  </motion.div>
                )}
              </form>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="contact-info-section"
            >
              <h2 className="section-title mb-8">
                Get in <span className="text-primary-blue">Touch</span>
              </h2>

              <div className="contact-info-list">
                {contactInfo.map((info, index) => (
                  <motion.div
                    key={info.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className={`contact-info-item ${info.action ? 'clickable' : ''}`}
                    onClick={info.action}
                  >
                    <div
                      className="contact-icon"
                      style={{ backgroundColor: `#2563eb20`, color: '#2563eb' }}
                    >
                      {info.icon}
                    </div>
                    <div className="contact-details">
                      <p>{info.value}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="quick-contact-buttons">
                <motion.button
                  className="btn btn-primary btn-lg"
                  onClick={() => handleContactClick('phone')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FaPhone />
                  Call Now
                </motion.button>

                <motion.button
                  className="btn btn-outline btn-lg"
                  onClick={() => handleContactClick('whatsapp')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FaWhatsapp />
                  WhatsApp
                </motion.button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="map-section py-20 bg-light-gray">
        <div className="container">
          <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="section-title">
              Our <span className="text-primary-blue">Location</span>
            </h2>
            <p className="section-subtitle">
              We serve the entire Qatar region with immediate mobilization
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="map-container"
          >
            <div className="map-embed-responsive">
              <iframe
                title="Location Map"
                src="https://www.google.com/maps?q=25.2721447,51.4088538&z=17&output=embed"
                width="100%"
                height="400"
                style={{ border: 0, borderRadius: '1rem' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>

          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact; 